import React, {useState, useEffect, useRef} from 'react';
import {useRouter} from 'next/router';
import moment from 'moment-timezone';
import {SparklesIcon} from '@heroicons/react-v2/24/solid';
import {LockClosedIcon} from '@heroicons/react/solid';
import _ from 'lodash';

import Tooltip from '../components/Tooltip';
import PageContainer from '../components/PageContainer';
import Modal from '../components/ModalNew';
import DuetSchedulingModal, {Stages, PracticeTypes} from './DuetSchedulingModal';
import DiscoveryCarousel, {
  PeerMockCard,
  AIQuestionCard,
  InterviewTypeFilter,
} from './DiscoveryCarousel';
import DuetJoiningModal from './DuetJoiningModal';
import DuetPracticeWithAIModal from './DuetPracticeWithAIModal';
import DuetFeedbackModal from './DuetFeedbackModal';
import DuetSettings from './DuetSettings';
import DuetPeerMockCreditCounter from './DuetPeerMockCreditCounter';
import SessionsTable, {
  UpcomingRequestControls,
  FeedbackCell,
} from './SessionsTable';
import DuetFAQs from './DuetFAQs';
import getLandingPageCopy from './DuetLandingPageCopy';
import Link from '../components/Link';
import DocumentHeader from '../components/DocumentHeader';
import SignupModal from '../components/SignupModal';
import Button, {ButtonSizes, ButtonTypes} from '../components/ButtonNew';
import Testimonial from '../components/TestimonialCard';
import FAQSection from '../components/FAQSection';
import WelcomePrampUserModal from '../components/WelcomePrampUserModal';
import {ShortRightArrowIcon} from '../components/svg';
import {
  createRequest,
  cancelRequest,
  createOnDemandSession,
} from '../api/api';
import {useAppContext, useAnalytics, useNotifications} from '../hooks';
import defaultAvatar from '../../assets/images/duet/matching.svg';
import {
  isWithinJoinTimeForSession,
  hideAIPracticeFeatures,
  hideAIFeedbackFeatures,
  createURLWithParams,
  getUpcomingTimesForType,
  isFinanceUser,
  cx,
} from '../util';
import {
  TRAILING_MATCH_TIMES,
  InterviewTypes,
  getInterviewTypesForUser,
  InterviewModes,
} from '../../../lib/constants/duet';

const AIUpsellLink = ({className}) => (
  <Link
    href="/upgrade?src=duet-1p-ai-header"
    className={cx(
      'px-2 py-1.5 border border-gray-300 rounded-full inline-flex items-center text-gray-500 font-medium',
      className,
    )}
  >
    <SparklesIcon className="w-4 h-4 mr-1 text-indigo" />
    Introducing our AI Interviewer
    <div className="text-indigo ml-1.5"> Try it out</div>
    <ShortRightArrowIcon className="h-3 w-3 mx-1" />
  </Link>
);

const FAQContent = ({contentId, hideAIFeedback}) => (contentId === 'ai' ? (
  <>
    <FAQSection items={DuetFAQs.AIGeneral} />
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-16">Interviewing</h3>
    <FAQSection items={DuetFAQs.AIInterviewing} />
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-16">Troubleshooting</h3>
    <FAQSection items={DuetFAQs.AITroubleshooting} />
  </>
) : (
  <>
    <FAQSection items={DuetFAQs.P2PGeneral.filter((faq) => !(hideAIFeedback && faq.ai))} />
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-16">Matching</h3>
    <FAQSection items={DuetFAQs.P2PMatching} />
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-16">Interviewing</h3>
    <FAQSection items={DuetFAQs.P2PInterviewing} />
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mt-16">Troubleshooting</h3>
    <FAQSection items={DuetFAQs.P2PTroubleshooting} />
  </>
));

const DuetLandingPage = ({
  contentId,
  nextMatchTimes,
  inProgressSession = null,
  freePeerSessions: initialFreePeerSessions = null,
  freeAISessions = null,
  upcomingRequests: initialUpcomingRequests = [],
  prevSessions = [],
  aiPracticeQuestions = [],
  pendingRequestsByType = {},
}) => {
  const {
    currentUser, hasSubscription, hasAiAccess, loading,
  } = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();
  const {setError} = useNotifications();
  const pageCopy = getLandingPageCopy(
    contentId,
    hideAIPracticeFeatures(currentUser),
    hideAIFeedbackFeatures(currentUser),
  );
  const [pageTitle, setPageTitle] = useState(pageCopy.title);
  const [upcomingRequests, setUpcomingRequests] = useState(initialUpcomingRequests);
  const [freePeerSessions, setFreePeerSessions] = useState(initialFreePeerSessions);
  const [isScheduling, setIsScheduling] = useState(false);
  const [schedulePrefill, setSchedulePrefill] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isStartingAISession, setIsStartingAISession] = useState(false);
  const [isTestingAV, setIsTestingAV] = useState(false);
  const [requestToJoin, setRequestToJoin] = useState(null);
  const [feedbackOpenFor, setFeedbackOpenFor] = useState(null);
  const [feedbackTabOpen, setFeedbackTabOpen] = useState(null);
  const [showRejoin, setShowRejoin] = useState(Boolean(inProgressSession));
  const [aiMockCarouselFilter, setAiMockCarouselFilter] = useState('all');
  const FaqElement = useRef();
  const hasUnlimitedCredits = currentUser?.data?.unlimited_credits_from_pramp || hasSubscription;
  const canSchedulePeerSession = Boolean(hasUnlimitedCredits || freePeerSessions > 0);
  const canCreateAISession = Boolean( // TODO: Account for member rate limiting?
    (hasAiAccess || freeAISessions > 0),
  );
  const showSchedulingModal = Boolean(currentUser && isScheduling);
  const showJoiningModal = Boolean(currentUser && isJoining);
  const showPracticeWithAIModal = Boolean(currentUser && isStartingAISession);
  const showPracticeWithAIButton = !hideAIPracticeFeatures(currentUser);
  const hasUsedDuet = Boolean(upcomingRequests.length > 0 || prevSessions.length > 0);
  const isFinanceLandingPage = isFinanceUser(currentUser?.profile)
    || contentId === InterviewTypes['Investment-Banking'].key;

  useEffect(() => {
    tracker.duetViewLanding();
  }, []);

  // Initialize discovery carousel
  useEffect(() => {
    if (isFinanceLandingPage) {
      setAiMockCarouselFilter(InterviewTypes['Investment-Banking'].key);
    }
  }, [isFinanceLandingPage]);

  useEffect(() => {
    if (router.isReady) {
      if (router.query.feedback) {
        setFeedbackOpenFor(_.find(prevSessions, (s) => s.room === router.query.feedback));
        setFeedbackTabOpen(router.query.feedback_tab);
      } else if (router.query.av_test) {
        setIsTestingAV(true);
      }
    }
  }, [router.isReady]);

  useEffect(() => {
    const updateTimeDependentState = () => {
      // Check if user has a request for the current session
      const requestForCurrSession = _.find(upcomingRequests,
        (r) => isWithinJoinTimeForSession(r.sessionTime));
      setRequestToJoin(requestForCurrSession || null);

      // Clean up upcoming requests that have passed
      const upcomingRequestsWithPassedRemoved = _.filter(upcomingRequests, (r) => moment().isBefore(moment(r.sessionTime).add(_.last(TRAILING_MATCH_TIMES), 'seconds')));
      if (upcomingRequestsWithPassedRemoved.length !== upcomingRequests.length) {
        setUpcomingRequests(upcomingRequestsWithPassedRemoved);
      }
    };

    updateTimeDependentState();
    const intervalId = setInterval(updateTimeDependentState, 1000);

    return () => clearInterval(intervalId);
  }, [upcomingRequests]);

  const handleScheduleRequest = async ({type, data, sessionTime}) => {
    const request = await createRequest({
      type,
      data,
      sessionTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    setFreePeerSessions((prev) => prev - 1);
    setUpcomingRequests(_.sortBy(_.concat(upcomingRequests, request), 'sessionTime'));
    const isJoiningCurrentSession = isWithinJoinTimeForSession(sessionTime);
    if (isJoiningCurrentSession) {
      setRequestToJoin(request);
      setIsScheduling(false);
      setIsJoining(true);
    }

    tracker.duetScheduleRequest({sessionTime, type, isJoiningCurrentSession});

    return request;
  };

  const handleCancelRequest = async ({id, sessionTime, type}) => {
    if (window.confirm(`Are you sure you want to cancel your ${InterviewTypes[type].name} interview session on ${moment(sessionTime).format('dddd, MMMM D [at] h:mm A')}?`)) {
      await cancelRequest({sessionTime, type});
      setFreePeerSessions((prev) => prev + 1);
      setUpcomingRequests(_.filter(upcomingRequests, (r) => r.id !== id));
      tracker.duetCancelRequest({sessionTime, type});
    }
  };

  const handleReschedule = () => {
    setIsScheduling(true);
    setIsJoining(false);
    tracker.duetNoMatchReschedule();
  };

  const handleCreateOnDemandSession = async (invitedEmail, interviewType) => {
    const roomId = await createOnDemandSession({invitedEmail, interviewType});
    tracker.duetCreateOnDemandSession({invitedEmail, type: interviewType});
    router.push(`/practice/${roomId}?onDemand=${interviewType}&initiated=1`);
  };

  const handleCreateAISession = async (interviewType, question = null) => {
    try {
      tracker.duetCreateAiSession({type: interviewType});
      router.push(createURLWithParams('/practice/ai/new', {type: interviewType, question}));
    } catch (error) {
      setError(error);
    }
  };

  const handleTestAV = () => {
    setIsTestingAV(true);
    tracker.duetClickTestAv();
  };

  const handleViewFeedback = (session) => {
    const tab = session.mode === InterviewModes.AI ? 'ai' : 'peer';
    setFeedbackOpenFor(session);
    setFeedbackTabOpen(tab);
  };

  const handleSelectPeerMockSession = (type, sessionTime) => {
    tracker.duetClickDiscoveryCarouselPeerMock({type, sessionTime});
    if (currentUser) {
      setSchedulePrefill({
        stage: Stages.SCHEDULE,
        draft: {
          mode: PracticeTypes.PEER.key,
          type,
          sessionTime,
        },
      });
    }
    setIsScheduling(true);
  };

  // Carousel: clicking an AI card starts a single-player AI session for that
  // type. Logged-out users get the signup modal instead.
  const handleSelectAIQuestion = (type, question) => {
    tracker.duetClickDiscoveryCarouselAiQuestion({type, question});
    if (currentUser) {
      handleCreateAISession(type, question);
    } else {
      setIsStartingAISession(true);
    }
  };

  const handleSelectAIType = (typeKey) => {
    tracker.duetFilterDiscoveryCarousel({carousel: 'ai', type: typeKey});
    setAiMockCarouselFilter(typeKey);
  };

  const matchesType = (selectedKey, typeKey) => selectedKey === 'all' || selectedKey === typeKey;

  // A per-type landing page (e.g. /practice/sql-mock-interviews) leads with its
  // own interview type in the DiscoveryCarousel
  const pageInterviewType = InterviewTypes[contentId];
  const promotePageType = (types) => (pageInterviewType
    ? _.sortBy(types, (t) => (t.key === pageInterviewType.key ? 0 : 1))
    : types);
  const availableTypes = getInterviewTypesForUser(
    isFinanceLandingPage ? {career_tracks: ['finance']} : currentUser?.profile, // Stub finance user_profile if necessary
  );
  const peerMockTypes = promotePageType(
    availableTypes
      .filter((t) => !t.disabled)
      .filter((t) => getUpcomingTimesForType(t.key, nextMatchTimes).length > 0),
  );
  const availableAiMockFilters = promotePageType(
    availableTypes
      .filter((t) => !t.disabled)
      .filter((t) => aiPracticeQuestions.some((q) => q.type === t.key)),
  );
  const aiMockQuestions = aiPracticeQuestions
    .filter((q) => matchesType(aiMockCarouselFilter, q.type));

  const discoveryCarousels = (
    <div className="w-full py-16 md:py-24 flex flex-col gap-12">
      <DiscoveryCarousel
        title="Join an upcoming session"
        subtitle="Grab an open seat in a live peer mock interview coming up this week."
      >
        {peerMockTypes.map((type) => (
          <PeerMockCard
            key={type.key}
            type={type}
            times={getUpcomingTimesForType(type.key, nextMatchTimes)}
            pendingRequests={pendingRequestsByType[type.key] || []}
            onSelect={handleSelectPeerMockSession}
          />
        ))}
      </DiscoveryCarousel>
      {showPracticeWithAIButton && (
        <DiscoveryCarousel
          title="Or practice with AI immediately"
          subtitle="Skip the wait — answer real interview questions with instant AI feedback, any time."
          filters={(
            <InterviewTypeFilter
              types={availableAiMockFilters}
              typeKey={aiMockCarouselFilter}
              onSelect={handleSelectAIType}
            />
          )}
        >
          {aiMockQuestions.map((question) => (
            <AIQuestionCard
              key={`${question.type}:${question.slug}`}
              question={question}
              onSelect={handleSelectAIQuestion}
            />
          ))}
        </DiscoveryCarousel>
      )}
    </div>
  );

  return (
    <PageContainer stickyHeader={!currentUser} className="overflow-x-hidden">
      <DocumentHeader
        title={pageTitle}
        socialDescription={pageCopy.socialDescription}
      />
      <WelcomePrampUserModal />
      <SignupModal
        isOpen={!currentUser && (isScheduling || isJoining || isStartingAISession)}
        source="duet"
      />
      <DuetSchedulingModal
        isOpen={showSchedulingModal}
        setPageTitle={setPageTitle}
        onScheduleRequest={handleScheduleRequest}
        onCreateOnDemandSession={handleCreateOnDemandSession}
        onClose={() => {
          setIsScheduling(false);
          setSchedulePrefill(null);
        }}
        nextMatchTimes={nextMatchTimes}
        upcomingRequests={upcomingRequests}
        canScheduleInterview={canSchedulePeerSession}
        prefill={schedulePrefill}
      />
      <DuetJoiningModal
        isOpen={showJoiningModal}
        setPageTitle={setPageTitle}
        onClose={() => setIsJoining(false)}
        onReschedule={handleReschedule}
        nextMatchTimes={nextMatchTimes}
        requestToJoin={requestToJoin}
      />
      <DuetPracticeWithAIModal
        isOpen={showPracticeWithAIModal}
        onClose={() => setIsStartingAISession(false)}
        onCreateAISession={handleCreateAISession}
      />
      <DuetFeedbackModal
        isOpen={Boolean(currentUser && feedbackOpenFor)}
        onClose={() => setFeedbackOpenFor(null)}
        session={feedbackOpenFor}
        initialTab={feedbackTabOpen}
      />
      <Modal
        header="Lights, camera, action!"
        isOpen={isTestingAV}
        showCloseButton
        onClose={() => setIsTestingAV(false)}
      >
        <p className="text-base text-center mb-6">
          Set up your camera so your partner will be able to see you.
          Click “Allow” when prompted to give camera access.
        </p>
        <DuetSettings onSubmit={() => setIsTestingAV(false)} submitText="Save" />
      </Modal>
      {inProgressSession && (
        <Modal
          isOpen={showRejoin}
          onClose={() => setShowRejoin(false)}
          showCloseButton
        >
          <div className="flex flex-col items-center">
            <div className="mt-4 h-16 w-16 rounded-full">
              {inProgressSession.partner.profileImage ? <img src={inProgressSession.partner.profileImage} alt="partner avatar" /> : <img src={defaultAvatar.src} alt="default avatar" />}
            </div>
            <div className="my-4 text-center text-lg  font-semibold text-gray-900">
              You have an interview in progress!
            </div>
            <Button
              href={`/practice/${inProgressSession.room}`}
              size={ButtonSizes.LARGE}
              onClick={() => tracker.duetRejoinMatch()}
            >
              Rejoin your mock interview
            </Button>
            <div className="text-sm text-center mt-8">
              Had issues with your session or already finished?
              <br />
              <Link
                href={`/practice/feedback/${inProgressSession.room}`}
                className="text-indigo"
              >
                Give us feedback to help us improve!
              </Link>
            </div>

          </div>
        </Modal>
      )}

      <div className={cx('w-full overflow-hidden', {'bg-white': hasUsedDuet})}>
        <div className={cx('max-w-6xl mx-auto px-4 lg:px-8 flex flex-col-reverse justify-between my-8 pb-1 md:flex-row', {'md:my-16': !hasUsedDuet})}>
          <div className={cx('flex flex-col shrink-0 justify-center', {'md:w-7/12 md:pr-10': !hasUsedDuet})}>
            <div className="max-w-xl">
              {!currentUser && <AIUpsellLink className="mb-4" />}
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight">
                {pageCopy.hero.header}
              </h1>
              <div className="text-lg my-4">
                {pageCopy.hero.description}
              </div>
            </div>

            <div className="flex items-center flex-col flex-wrap md:flex-row">
              {currentUser ? (
                <>
                  {requestToJoin && (
                    <Button
                      size={ButtonSizes.LARGE}
                      onClick={() => setIsJoining(true)}
                      className="whitespace-nowrap w-full mb-3 md:w-auto mr-4"
                    >
                      Join session
                    </Button>
                  )}
                  {canSchedulePeerSession && (
                    <Button
                      size={ButtonSizes.LARGE}
                      onClick={() => setIsScheduling(true)}
                      buttonType={requestToJoin ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY}
                      className="whitespace-nowrap w-full mb-3 md:mr-4 md:w-auto"
                    >
                      {showPracticeWithAIButton ? 'Schedule peer mock interview' : 'Schedule practice session'}
                    </Button>
                  )}
                  {!canSchedulePeerSession && (
                    <Tooltip content="Upgrade for unlimited mock interviewing" placement="top">
                      <span className="w-full md:w-auto">
                        <Button
                          size={ButtonSizes.LARGE}
                          buttonType={ButtonTypes.SECONDARY}
                          onClick={() => tracker.duetClickUpgrade()}
                          className="whitespace-nowrap w-full mb-3 md:mr-4 md:w-auto"
                          href="/upgrade?src=duet-peer-mocks"
                        >
                          <LockClosedIcon className="mr-1.5 h-4 w-4" />
                          {showPracticeWithAIButton ? 'Schedule peer mock interview' : 'Schedule practice session'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {showPracticeWithAIButton && canCreateAISession && (
                    <Button
                      size={ButtonSizes.LARGE}
                      onClick={() => setIsStartingAISession(true)}
                      buttonType={ButtonTypes.SECONDARY}
                      className="whitespace-nowrap w-full mb-3 md:w-auto"
                    >
                      Start an AI interview
                    </Button>
                  )}
                  {showPracticeWithAIButton && !canCreateAISession && (
                    <Tooltip content="Upgrade for unlimited AI interviewing" placement="top">
                      <span className="w-full md:w-auto">
                        <Button
                          size={ButtonSizes.LARGE}
                          buttonType={ButtonTypes.SECONDARY}
                          onClick={() => tracker.duetClick1PAiUpsell()}
                          className="whitespace-nowrap w-full mb-3 md:w-auto"
                          href="/upgrade?src=duet-1p-ai"
                        >
                          <LockClosedIcon className="mr-1.5 h-4 w-4" />
                          Start an AI interview
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  <Button
                    size={ButtonSizes.LARGE}
                    onClick={() => setIsScheduling(true)}
                    className="whitespace-nowrap w-full mb-4 md:w-auto md:mr-5"
                  >
                    {pageCopy.hero.cta}
                  </Button>
                  <button
                    type="button"
                    onClick={() => FaqElement.current.scrollIntoView({behavior: 'smooth'})}
                    className="text-gray-500 font-medium whitespace-nowrap mb-4"
                  >
                    See how it works
                  </button>
                </>
              )}
            </div>
          </div>
          {!hasUsedDuet && pageCopy.hero.image}
        </div>

        {hasUsedDuet && (
          <div className="max-w-6xl mx-auto px-4 lg:px-8 items-center">
            {!loading && !hasUnlimitedCredits && (
              <DuetPeerMockCreditCounter
                freePeerSessions={freePeerSessions}
                onClick={() => tracker.duetClickUpgrade()}
              />
            )}

            <div className="mb-12">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold">Your sessions</h3>
                <button
                  type="button"
                  onClick={handleTestAV}
                  className="hidden md:block text-indigo font-semibold p-1"
                >
                  Test AV
                </button>
              </div>
              <SessionsTable
                upcomingRequests={upcomingRequests}
                prevSessions={prevSessions}
                renderAction={(row) => (row.kind === 'upcoming' ? (
                  <UpcomingRequestControls
                    request={row.data}
                    onClickJoin={() => setIsJoining(true)}
                    onClickCancel={() => handleCancelRequest(row.data)}
                  />
                ) : (
                  <FeedbackCell onViewFeedback={() => handleViewFeedback(row.data)} />
                ))}
              />
            </div>
          </div>
        )}
      </div>

      <div className="w-full [&>*:nth-child(odd)]:bg-gray-50 [&>*:nth-child(even)]:bg-white">
        {discoveryCarousels}

        {!hasUsedDuet && <WhosUsingIt pageCopy={pageCopy} />}

        <HowItWorks pageCopy={pageCopy} />

        {!hasUsedDuet && <WhyPractice pageCopy={pageCopy} onClick={() => setIsScheduling(true)} />}

        <Testimonials pageCopy={pageCopy} />

        <div className="w-full py-16 md:py-24 px-4 flex flex-col items-center">
          <div className="w-full max-w-3xl">
            <h2 ref={FaqElement} className="text-xl font-extrabold text-black md:text-2xl md:text-left">
              Frequently asked questions
            </h2>
            <FAQContent
              contentId={contentId}
              hideAIFeedback={hideAIFeedbackFeatures(currentUser)}
            />
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

const WhosUsingIt = ({pageCopy}) => (pageCopy.whosUsingIt ? (
  <div className="w-full py-16 md:py-24">
    <div className="mx-auto max-w-6xl px-4 lg:px-8 flex flex-col md:flex-row">
      {pageCopy.whosUsingIt.image}
      <div className="">
        <h2 className="text-base font-semibold leading-7 text-indigo-600">
          {'Who\'s using it'}
        </h2>
        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {pageCopy.whosUsingIt.header}
        </p>
        <p className="mt-6 max-w-xl text-base leading-7 text-gray-600">
          {pageCopy.whosUsingIt.description}
        </p>
      </div>
    </div>
  </div>
) : null);

const HowItWorks = ({pageCopy}) => (pageCopy.howItWorks ? (
  <div className="w-full py-16 md:py-24">
    <div className="mx-auto max-w-6xl px-4 lg:px-8">
      <div className="mx-auto max-w-2xl md:text-center">
        <h2 className="text-base font-semibold leading-7 text-indigo-600">
          How it works
        </h2>
        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {pageCopy.howItWorks.header}
        </p>
      </div>
      <div className="mx-auto mt-10 max-w-6xl">
        <dl className="grid grid-cols-1 gap-y-8 gap-x-12 md:grid-cols-3">
          {pageCopy.howItWorks.instructions.map((instruction) => (
            <div key={instruction.name} className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                <instruction.icon className="h-5 w-5 flex-none text-indigo-600" aria-hidden="true" />
                {instruction.name}
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                <p className="flex-auto">{instruction.description}</p>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </div>
) : null);

const WhyPractice = ({pageCopy, onClick}) => (pageCopy.whyPractice ? (
  <div className="w-full py-16 md:py-24">
    <div className="mx-auto max-w-6xl px-4 lg:px-8 grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start">
      <div className="lg:pr-4 lg:pt-4">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            Why practice?
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {pageCopy.whyPractice.header}
          </p>
          <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-600 lg:max-w-none">
            {pageCopy.whyPractice.features.map((feature) => (
              <div key={feature.name} className="relative pl-9">
                <feature.icon className="absolute left-1 top-1 h-5 w-5 text-indigo-600" aria-hidden="true" />
                <p className="inline">
                  {feature.description}
                </p>
              </div>
            ))}
          </dl>
          <Button
            size={ButtonSizes.LARGE}
            onClick={onClick}
            className="w-full md:w-auto mt-10 "
          >
            {pageCopy.whyPractice.cta}
          </Button>
        </div>
      </div>
      {pageCopy.whyPractice.image}
    </div>
  </div>
) : null);

const Testimonials = ({pageCopy}) => (pageCopy.testimonials ? (
  <div className="w-full py-16 md:py-24">
    <div className="mx-auto max-w-6xl px-4 lg:px-8">
      <h2 className="text-center text-3xl tracking-tight font-extrabold text-gray-900 sm:text-4xl md:text-5xl">
        Testimonials
      </h2>
      <p className="mx-auto text-center mt-3 mb-10 max-w-xl text-base font-regular text-gray-500 sm:text-lg md:mt-5 md:text-xl">
        {pageCopy.testimonials.description}
      </p>
      <div className="flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Top review section */}
          {pageCopy.testimonials.testimonials.map((testimonial) => (
            <Testimonial testimonial={testimonial} key={testimonial.name} />
          ))}
        </div>
      </div>
    </div>
  </div>
) : null);

export default DuetLandingPage;
